<script>
/**
 * Specific meeting pages constants (which require additional processing)
 */

// Mixins
import mixinPbOptions from '@/mixins/page-options/problems.vue';
import mixinPendingOptions from '@/mixins/page-options/pending.vue';
import mixinActionsOptions from '@/mixins/page-options/actions.vue';
import mixinIdcOptions from '@/mixins/page-options/indicators.vue';
import mixinStatsOptions from '@/mixins/page-options/stats.vue';
import mixinMgtIdcOptions from '@/mixins/page-options/mgt-indicators.vue';
import mixinMgtPagesOptions from '@/mixins/page-options/mgt-pages.vue';
import mixinInfosOptions from '@/mixins/page-options/infos.vue';
import mixinDashboardOptions from '@/mixins/page-options/dashboard.vue';
import mixinGwTemplatesOptions from '@/mixins/page-options/gw-templates.vue';
import mixinGwChecklistsOptions from '@/mixins/page-options/gw-checklists.vue';
import mixinCompletedGwOptions from '@/mixins/page-options/gemba-walks.vue';
import mixinGwStatsOptions from '@/mixins/page-options/gw-stats.vue';
// Utils
import SPage from '@/utils/classes/SPage';
import { getAllPageLinks } from '@/utils/meeting-utils';

export default {

  mixins: [
    mixinPbOptions,
    mixinPendingOptions,
    mixinActionsOptions,
    mixinIdcOptions,
    mixinStatsOptions,
    mixinMgtIdcOptions,
    mixinMgtPagesOptions,
    mixinInfosOptions,
    mixinDashboardOptions,
    mixinGwTemplatesOptions,
    mixinGwChecklistsOptions,
    mixinCompletedGwOptions,
    mixinGwStatsOptions,
  ],

  props: {
    noMeetingPages: Boolean,
  },

  computed: {
    /**
     * All available pages in a page-picker
     * Returns an EditableLabelRadio-formatted array with items format:
     * {
     *   id: <constant_number>,
     *   link: <constant_str>,
     *   title: string,
     *   olderId: ID of the previous version of 'pageLinks'
     * }
     */
    pageLinks() {
      if (this.currentTeam) {
        const { hierarchyLevel, accessGW } = this.currentTeam;
        return getAllPageLinks(hierarchyLevel <= 0, this.noMeetingPages, accessGW !== 1, !this.noMeetingPages)
      }
      return getAllPageLinks();
    },
  },

  methods: {
    /**
     * Get a route link from a page id
     * @param {Number} pageObject - object from SPage constants featuring 'pageId' and 'pageOptions' params
     * @return {String} - Route link
     */
    getPageLink(pageObject) {
      if (!pageObject) {
        return '';
      }
      const { pageId, pageOptions } = pageObject;
      let qParams = '';
      const pageItem = SPage.fromId[`${pageId}`];
      if (!pageItem) {
        return '';
      }

      // Additional options
      let querySuffix = '';
      if (typeof pageItem.queryOptions !== 'undefined') {
        querySuffix = Object.keys(pageItem.queryOptions).reduce(
          (acc, key) => `${acc}${acc === ''
            ? ''
            : '&'}${key}=${pageItem.queryOptions[key]}`,
          '',
        );
      }
      // Handle specific cases
      if (typeof pageItem.queryIdKey !== 'undefined') {
        const itemId = pageOptions && pageOptions.itemId ? pageOptions.itemId : pageObject.itemId;
        querySuffix = querySuffix === '' ? '' : `&${querySuffix}`;
        return `${pageItem.link}?${pageItem.queryIdKey}=${itemId}${querySuffix}`;
      }
      if (pageItem.id === SPage.PROBLEMS.id) {
        qParams = this.pbOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.PENDING.id) {
        qParams = this.pendOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.ACTION_PLANS.id) {
        qParams = this.actOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.INDICATORS.id) {
        qParams = this.idcOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.STATS.id) {
        qParams = this.statsOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.MGT_INDICATORS.id) {
        qParams = this.mgtIdcOptQueryParams(pageOptions);
      } else if ([SPage.MGT_PROBLEMS.id, SPage.MGT_ACTIONS.id, SPage.MGT_STATS.id].includes(pageItem.id)) {
        qParams = this.mgtPagesOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.INFORMATIONS.id) {
        qParams = this.infosOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.DASHBOARD.id) {
        qParams = this.dashboardOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.GW_TEMPLATES.id) {
        qParams = this.gwtOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.GW_CHECKLISTS.id) {
        qParams = this.gwChecklistsOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.GW_COMPLETED_LIST.id) {
        qParams = this.completedGwOptQueryParams(pageOptions);
      } else if (pageItem.id === SPage.GW_STATS.id) {
        qParams = this.gwStatsOptQueryParams(pageOptions);
      }
      return `${pageItem.link}${qParams === '' ? '' : `?${qParams}`}`;
    },
  },
};
</script>
